import { Typography } from '@mui/material';
import * as api from "api/Core";
import { GlobalClassKeys } from 'assets/jss/common-styles';
import { SpinnerIndicator } from 'components/CallServer';
import RequestErrorDialog from 'components/CallServer/RequestErrorDialog';
import { RequestErrorReport } from 'components/CallServer/RequestErrorReport';
import IconThumbUp from 'components/Icons/IconThumbUp';
import IconUserSpecialist from 'components/Icons/IconUserSpecialist';
import MuiRegularButton from 'components/MuiExtras/Button/MuiRegularButton';
import { GridEx } from 'components/MuiExtras/Grid/GridEx';
import MuiClickable from 'components/MuiExtras/MuiClickable';
import MuiConfirmation from 'components/MuiExtras/MuiConfirmation';
import MuiContainer from 'components/MuiExtras/MuiContainer';
import MuiLink from 'components/MuiExtras/MuiLink';
import MuiWidgetTitle from 'components/MuiExtras/MuiWidgetTitle';
import MuiColor from 'components/MuiExtras/Typography/MuiColor';
import { withSnackbar, WithSnackbar } from 'components/SnackbarProvider/Snackbar';
import { UserKnowsAboutSpecialistGrantMethod } from 'models/ApiEnums';
import { PagedResult, UserKnowsAboutGetDto } from 'models/ApiModels';
import { RequestLifeCycle } from 'models/AppModels';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import Session from 'services/Session';
import { GetEndorseMessageLine } from 'views/User/Components/UserEditKnowsAbout';
import UserEndorsementListDrawer from 'views/User/Dialogs/UserEndorsementListDrawer';

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    list: PagedResult<UserKnowsAboutGetDto>;
    formCycle: RequestLifeCycle;
    lastError: any;
    take: number;
    dialog: any;
}

interface IProps {
    userId: string;
    username: string;
}

type Props = IProps & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

class WidgetKnowsAbout extends React.PureComponent<Props, IState> {
    constructor(props: Props) {
        super(props);
        this.state = {
            dialog: null,
            list: null,
            take: 0,
            formCycle: RequestLifeCycle.Loading,
            lastError: null
        };
    }

    ///
    private loadData(offset: number, take: number) {
        this.setState({ formCycle: RequestLifeCycle.Loading, dialog: null });

        api.getUserKnowsAbout(this.props.userId, offset, take)
            .then((list) => {
                this.setState({ list, formCycle: RequestLifeCycle.None, take: take });
            })
            .catch((error) => {
                this.setState({ formCycle: RequestLifeCycle.LoadFailed, lastError: error });
            });
    }

    ///
    private renderLoadMore() {
        const { list } = this.state;

        if (list.total < 4)
            return null;

        return list.items.length == 3
            ? <MuiRegularButton color="secondary" fullWidth link onClick={() => this.loadData(0, list.total)}>{this.props.t("Common.Feed.Literals.ShowAllText")}</MuiRegularButton>
            : <MuiRegularButton color="secondary" fullWidth link onClick={() => this.loadData(0, 3)}>{this.props.t("Common.Feed.Literals.ShowLessText")}</MuiRegularButton>
            ;
    }

    ///
    private reload() {
        this.loadData(0, this.state.take);
    }

    ///
    private handleSpecialist(item: UserKnowsAboutGetDto) {
        this.setState({
            dialog: (
                <MuiConfirmation
                    message={
                        this.props.t(
                            item.specialist.grantedBy == null
                                ? "Views.User.Widgets.KnowsAbout.ConfirmationGrantSpecialistMessage"
                                : "Views.User.Widgets.KnowsAbout.ConfirmationRevokeSpecialistMessage"
                        )
                    }
                    onClose={
                        (result) => {
                            if (result) {
                                api.updateUserKnowsAbout(
                                    this.props.userId,
                                    {
                                        topicId: item.topicId,
                                        managerSpecialistOption: item.specialist.grantedBy == null
                                    })
                                    .then(() => this.reload())
                                    .catch((error) => {
                                        this.setState({ dialog: null, lastError: error, formCycle: RequestLifeCycle.PostFailed });
                                    });
                            }
                            else {
                                this.setState({ dialog: null });
                            }
                        }
                    }
                />
            )
        });
    }

    ///
    private handleEndorsement(item: UserKnowsAboutGetDto) {

        if (item.currentEndorser == null) {
            api.updateUserKnowsAbout(this.props.userId, { topicId: item.topicId, managerEndorsementOption: true })
                .then(() => this.reload());
        }
        else {

            this.setState({
                dialog: (
                    <MuiConfirmation
                        message={this.props.t("Views.User.Widgets.KnowsAbout.RemoveEndorsement")}
                        onClose={
                            (result) => {
                                if (result) {
                                    api.updateUserKnowsAbout(this.props.userId, { topicId: item.topicId, managerEndorsementOption: false })
                                        .then(() => this.reload())
                                        .catch((error) => {
                                            this.setState({ dialog: null, lastError: error, formCycle: RequestLifeCycle.PostFailed });
                                        });
                                }
                                else {
                                    this.setState({ dialog: null });
                                }
                            }
                        }
                    />
                )
            });
        }
    }

    ///
    private handleEndorseView = (topicId: string) => {
        this.setState({
            dialog: (
                <UserEndorsementListDrawer
                    userId={this.props.userId}
                    topicId={topicId}
                    onClose={() => this.setState({ dialog: null })}
                />
            )
        });
    };

    ///
    private renderItems() {
        const { list } = this.state;

        const readonly = this.props.userId == Session.user.id;

        return list.items.map((item, index) => {

            return (
                <MuiContainer margin="dense">
                    <GridEx key={index} container spacing={1} justifyContent="space-between" wrap="nowrap">
                        <GridEx item>
                            <MuiClickable
                                title={this.props.t("Views.User.Widgets.KnowsAbout.ThumbLinkTitle")}
                                disabled={readonly}
                                onClick={() => this.handleEndorsement(item)}
                            >
                                <MuiColor variant={item.currentEndorser != null ? "info" : "muted"}>
                                    <IconThumbUp />
                                </MuiColor>
                            </MuiClickable>
                        </GridEx>
                        <GridEx item xs zeroMinWidth alignSelf="center">

                            <Typography component="div" variant="subtitle2" className={GlobalClassKeys.normalLineHeight} >
                                <MuiColor variant="muted">
                                    {
                                        readonly || !item.topicAllowUserSpecialist || !item.endorserHandleSpecialist
                                            ? <b>{item.topicName}</b>
                                            : (
                                                <MuiLink
                                                    color="inherit"
                                                    title={this.props.t("Views.User.Widgets.KnowsAbout.SpecialistLinkTitle")}
                                                    onClick={() => this.handleSpecialist(item)}
                                                >
                                                    <b>{item.topicName}</b>
                                                </MuiLink>
                                            )
                                    }

                                    {
                                        item.topicAllowUserSpecialist && item.specialist.specialistGrantMethod != UserKnowsAboutSpecialistGrantMethod.None &&
                                        <span>
                                            <span>&nbsp;</span>
                                            <IconUserSpecialist
                                                active
                                                color="success"
                                                title={
                                                    item.specialist.grantedBy == Session.user.id
                                                        ? this.props.t("Views.User.Common.KnowsAbout.SpecialistByYou")
                                                        : item.specialist.specialistGrantMethod == UserKnowsAboutSpecialistGrantMethod.TopicRules
                                                            ? this.props.t("Views.User.Common.KnowsAbout.SpecialistByTopicRules")
                                                            : item.specialist.specialistGrantMethod == UserKnowsAboutSpecialistGrantMethod.TopicManager
                                                                ? this.props.t("Views.User.Common.KnowsAbout.SpecialistByTopicManager")
                                                                : this.props.t("Views.User.Common.KnowsAbout.SpecialistByUserManager")
                                                }
                                            />
                                        </span>
                                    }
                                </MuiColor>
                            </Typography>

                            {
                                item.endorsementsCount > 0 &&
                                <Typography component="div" variant="subtitle1" className={GlobalClassKeys.normalLineHeight}>
                                    <MuiColor variant="muted">
                                        <small>
                                            <MuiLink
                                                color="inherit"
                                                title={this.props.t("Views.User.Widgets.KnowsAbout.EndorseLinkTitle")}
                                                onClick={() => this.handleEndorseView(item.topicId)}
                                            >
                                                <GetEndorseMessageLine endorsementsCount={item.endorsementsCount} endorsed={item.currentEndorser != null} />
                                            </MuiLink>
                                        </small>
                                    </MuiColor>
                                </Typography>
                            }
                        </GridEx>
                    </GridEx>
                </MuiContainer>
            );
        });
    }

    ///
    private renderContent() {
        return (
            <>
                {this.renderItems()}
                {this.renderLoadMore()}
                <br />
            </>
        );
    }

    ///
    public componentDidMount() {
        this.loadData(0, 3);
    }

    ///
    public render() {
        const { list, formCycle, lastError, dialog } = this.state;

        if (formCycle == RequestLifeCycle.LoadFailed)
            return <RequestErrorReport error={lastError} />;

        if (formCycle == RequestLifeCycle.None && (list == null || list.total == 0))
            return null;

        return (
            <MuiContainer margin="normal" >
                <MuiWidgetTitle title={this.props.t("Views.User.Widgets.KnowsAbout.BoxTitle")} />

                <RequestErrorDialog
                    open={formCycle == RequestLifeCycle.PostFailed}
                    error={lastError}
                    onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                />

                {
                    formCycle == RequestLifeCycle.Loading
                        ? <SpinnerIndicator />
                        : this.renderContent()
                }

                {dialog}

            </MuiContainer >
        );
    }
}

export default withSnackbar(withTranslation()(WidgetKnowsAbout));